import type {
  LanguageRef,
  TranslationMemoryEntry,
} from '@src/repositories/TranslationMemoryEntryRepository';

/**
 * Shared, pure filtering helpers for the Translation Memory view. Lifted out of
 * `EntryTable` (PLAT-10846) so the entry table and the matrix (PLAT-10847) apply
 * the same search / language logic from one source of truth.
 */

/** Distinct, caption-sorted languages actually present on the given refs — so
 *  the filters (and matrix columns) only offer languages the entries use, not
 *  every workspace one. */
export const distinctLanguages = (
  refs: (LanguageRef | null | undefined)[],
): LanguageRef[] => {
  const byId = new Map<string, LanguageRef>();
  for (const ref of refs) if (ref && !byId.has(ref.id)) byId.set(ref.id, ref);
  return [...byId.values()].sort((a, b) =>
    (a.caption ?? a.value ?? a.id).localeCompare(b.caption ?? b.value ?? b.id),
  );
};

export interface EntryFilterCriteria {
  /** Free-text query over source term / translation / comment. */
  search: string;
  /** Source-language id, `''` for all source languages. */
  sourceId: string;
  /** Target-language id, `''` for all target languages. */
  targetId: string;
}

/**
 * Filter entries by free-text search (source term / translation / comment) and
 * by source / target language, all AND-combined. An entry with an empty
 * target-language link applies to all target languages, so it matches any
 * target filter.
 */
export const filterEntries = (
  entries: TranslationMemoryEntry[],
  { search, sourceId, targetId }: EntryFilterCriteria,
): TranslationMemoryEntry[] => {
  const q = search.trim().toLowerCase();
  return entries.filter(
    e =>
      (!q ||
        e.sourceTerm.toLowerCase().includes(q) ||
        e.translation.toLowerCase().includes(q) ||
        (e.comment ?? '').toLowerCase().includes(q)) &&
      (!sourceId || e.sourceLanguage?.id === sourceId) &&
      (!targetId ||
        e.translationLanguage?.id === targetId ||
        e.translationLanguage == null),
  );
};
